import type {
  ManualConsumptionFields,
  PbevVehicle,
  VehicleMatch,
} from '@/types';

export const downloadFile = (data: Blob, filename: string) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const formatDate = (date: string | Date): string => {
  return new Date(date).toLocaleDateString();
};

export const formatDateTime = (date: string | Date): string => {
  return new Date(date).toLocaleString();
};

export const formatFileSize = (bytes: number): string => {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const formatPercentage = (value: number): string => {
  return `${(value * 100).toFixed(0)}%`;
};

const formatMetric = (
  label: string,
  value: number | undefined,
  unit: string
): string | null => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return null;
  }

  return `${label}: ${Number(value).toFixed(1)} ${unit}`;
};

export const formatManualConsumptionSummary = (
  manual?: ManualConsumptionFields | null
): string => {
  if (!manual) {
    return 'No approved manual consumption';
  }

  const parts = [
    formatMetric('Gas city', manual.consumo_gasolina_cidade, 'km/l'),
    formatMetric('Gas road', manual.consumo_gasolina_estrada, 'km/l'),
    formatMetric('Ethanol city', manual.consumo_alcool_cidade, 'km/l'),
    formatMetric('Ethanol road', manual.consumo_alcool_estrada, 'km/l'),
    formatMetric('Hybrid city', manual.consumo_hibrido_cidade, 'km/le'),
    formatMetric('Hybrid road', manual.consumo_hibrido_estrada, 'km/le'),
    formatMetric('Energy', manual.consumo_energetico, 'MJ/km'),
    formatMetric('Range', manual.autonomia_modo_eletrico, 'km'),
  ].filter(Boolean);

  return parts.length ? parts.join(' • ') : 'Manual record without quantified metrics';
};

export const getSourceHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const ELECTRIC_TOKEN_PATTERN = /(^|\s)(VE|BEV)(\s|$)/;
const HYBRID_TOKEN_PATTERN = /(^|\s)(HEV|PHEV|VEHP)(\s|$)/;

const normalizePbevText = (value?: string): string => {
  return value
    ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase()
    : '';
};

const getPbevBaseLabel = (vehicle: PbevVehicle | VehicleMatch): string => {
  return 'pbev_vehicle' in vehicle ? vehicle.pbev_vehicle : vehicle.vehicle;
};

const isPureElectricPbevVehicle = (vehicle: PbevVehicle | VehicleMatch): boolean => {
  const normalized = normalizePbevText(
    [vehicle.tipo_propulsao, vehicle.combustivel].filter(Boolean).join(' ')
  );

  if (!normalized) {
    return false;
  }

  const hasElectricHint =
    normalized.includes('ELETRIC') ||
    normalized.includes('100%') ||
    ELECTRIC_TOKEN_PATTERN.test(normalized);

  const hasHybridHint =
    normalized.includes('HIBRID') || HYBRID_TOKEN_PATTERN.test(normalized);

  return hasElectricHint && !hasHybridHint;
};

const formatPbevRangeLabel = (vehicle: PbevVehicle | VehicleMatch): string | null => {
  const range = vehicle.autonomia_modo_eletrico;
  if (range === undefined || range === null || Number.isNaN(Number(range))) {
    return null;
  }

  const rangeLabel = isPureElectricPbevVehicle(vehicle) ? 'Max range' : 'Electric range';
  return `${rangeLabel}: ${Math.round(range)} km`;
};

export const formatPbevVehicleLabel = (vehicle: PbevVehicle | VehicleMatch): string => {
  const parts = [
    getPbevBaseLabel(vehicle),
    vehicle.ano_referencia ? `Model year: ${vehicle.ano_referencia}` : null,
    vehicle.cambio ? `Transmission: ${vehicle.cambio}` : null,
    formatPbevRangeLabel(vehicle),
  ];

  return parts.filter(Boolean).join(' • ');
};

export const formatPbevVehicleSpecs = (vehicle: PbevVehicle | VehicleMatch): string => {
  const parts = [
    vehicle.versao ? `Version: ${vehicle.versao}` : null,
    vehicle.tipo_propulsao ? `Powertrain: ${vehicle.tipo_propulsao}` : null,
    vehicle.combustivel ? `Fuel: ${vehicle.combustivel}` : null,
  ];

  return parts.filter(Boolean).join(' • ');
};

export const calculateSimilarity = (str1: string, str2: string): number => {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const editDistance = (s1: string, s2: string): number => {
    const costs: number[] = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  const distance = editDistance(longer.toLowerCase(), shorter.toLowerCase());
  return (longer.length - distance) / longer.length;
};

export const filterVehicles = <T extends {
  vehicle: string;
  marca?: string;
  modelo?: string;
  motor?: string;
  versao?: string;
  tipo_propulsao?: string;
  cambio?: string;
  combustivel?: string;
  autonomia_modo_eletrico?: number;
  ano_referencia?: number;
  ano?: number;
  model_id?: number;
  fipe_id?: string;
  pbev_id?: number;
}>(
  vehicles: T[],
  search: string
): T[] => {
  if (!search) return vehicles;
  const term = search.toLowerCase();
  return vehicles.filter((v) =>
    v.vehicle.toLowerCase().includes(term) ||
    v.marca?.toLowerCase().includes(term) ||
    v.modelo?.toLowerCase().includes(term) ||
    v.motor?.toLowerCase().includes(term) ||
    v.versao?.toLowerCase().includes(term) ||
    v.tipo_propulsao?.toLowerCase().includes(term) ||
    v.cambio?.toLowerCase().includes(term) ||
    v.combustivel?.toLowerCase().includes(term) ||
    (v.autonomia_modo_eletrico !== undefined &&
      String(v.autonomia_modo_eletrico).toLowerCase().includes(term)) ||
    (v.ano_referencia !== undefined && String(v.ano_referencia).includes(term)) ||
    (v.ano !== undefined && String(v.ano).includes(term)) ||
    (v.model_id && String(v.model_id).toLowerCase().includes(term)) ||
    (v.fipe_id && String(v.fipe_id).toLowerCase().includes(term)) ||
    (v.pbev_id && String(v.pbev_id).toLowerCase().includes(term))
  );
};
